import Phaser from 'phaser'
import { Creature } from '../entities/Creature'
import { CreatureFactory } from '../entities/CreatureFactory'
import { Player } from '../entities/Player'
import {
  SpawnZone,
  SPAWN_CONFIG,
  TILES
} from '../types/GameTypes'

interface SpawnedCreature {
  creature: Creature
  zone: SpawnZone
  respawnTimer?: Phaser.Time.TimerEvent
}

export class SpawnSystem {
  private scene: Phaser.Scene
  private player: Player
  private groundData: number[][]
  private zones: SpawnZone[] = []
  private spawnedCreatures: SpawnedCreature[] = []
  private creatureGroup: Phaser.GameObjects.Group

  private tileSize: number = 32
  private mapWidth: number = 50
  private mapHeight: number = 50

  constructor(
    scene: Phaser.Scene,
    player: Player,
    groundData: number[][],
    creatureGroup: Phaser.GameObjects.Group
  ) {
    this.scene = scene
    this.player = player
    this.groundData = groundData
    this.creatureGroup = creatureGroup

    // Initialize spawn zones
    this.initializeZones()

    // Initial spawn
    this.performInitialSpawn()
  }

  /**
   * Initialize spawn zones across the map
   */
  private initializeZones(): void {
    const zoneSize = SPAWN_CONFIG.ZONE_SIZE

    for (let zy = 0; zy < Math.ceil(this.mapHeight / zoneSize); zy++) {
      for (let zx = 0; zx < Math.ceil(this.mapWidth / zoneSize); zx++) {
        const zoneX = zx * zoneSize
        const zoneY = zy * zoneSize

        // Skip town area
        if (this.isInTownArea(zoneX, zoneY, zoneSize)) {
          continue
        }

        // Check if zone has valid spawn tiles
        if (this.hasValidSpawnTiles(zoneX, zoneY, zoneSize)) {
          this.zones.push({
            x: zoneX,
            y: zoneY,
            width: zoneSize,
            height: zoneSize,
            creatureTypes: CreatureFactory.getAllTypes(),
            maxCreatures: SPAWN_CONFIG.MAX_PER_ZONE,
            currentCreatures: 0
          })
        }
      }
    }
  }

  /**
   * Check if zone overlaps with town area
   */
  private isInTownArea(zoneX: number, zoneY: number, zoneSize: number): boolean {
    const townMinX = SPAWN_CONFIG.TOWN_MIN_X
    const townMaxX = SPAWN_CONFIG.TOWN_MAX_X
    const townMinY = SPAWN_CONFIG.TOWN_MIN_Y
    const townMaxY = SPAWN_CONFIG.TOWN_MAX_Y

    const zoneMaxX = zoneX + zoneSize
    const zoneMaxY = zoneY + zoneSize

    // Check for overlap
    return !(zoneMaxX <= townMinX || zoneX >= townMaxX ||
             zoneMaxY <= townMinY || zoneY >= townMaxY)
  }

  /**
   * Check if zone has tiles suitable for spawning
   */
  private hasValidSpawnTiles(zoneX: number, zoneY: number, zoneSize: number): boolean {
    for (let y = zoneY; y < Math.min(zoneY + zoneSize, this.mapHeight); y++) {
      for (let x = zoneX; x < Math.min(zoneX + zoneSize, this.mapWidth); x++) {
        if (this.isValidSpawnTile(x, y)) {
          return true
        }
      }
    }
    return false
  }

  /**
   * Check if a specific tile is valid for spawning
   */
  private isValidSpawnTile(tileX: number, tileY: number): boolean {
    if (tileX < 0 || tileX >= this.mapWidth || tileY < 0 || tileY >= this.mapHeight) {
      return false
    }

    const tile = this.groundData[tileY][tileX]

    // Valid spawn tiles: grass, dirt, sand (not water, stone path, buildings)
    const validTiles = [
      TILES.GRASS1, TILES.GRASS2, TILES.GRASS3, TILES.GRASS_FLOWER,
      TILES.DIRT1, TILES.DIRT2,
      TILES.SAND1, TILES.SAND2
    ]

    return validTiles.includes(tile as typeof validTiles[number])
  }

  /**
   * Find a valid spawn position within a zone
   */
  private findSpawnPosition(zone: SpawnZone): { x: number; y: number } | null {
    const maxAttempts = 20

    for (let i = 0; i < maxAttempts; i++) {
      const tileX = zone.x + Math.floor(Math.random() * zone.width)
      const tileY = zone.y + Math.floor(Math.random() * zone.height)

      if (this.isValidSpawnTile(tileX, tileY)) {
        // Check not too close to player
        const worldX = tileX * this.tileSize + this.tileSize / 2
        const worldY = tileY * this.tileSize + this.tileSize / 2
        const distToPlayer = Phaser.Math.Distance.Between(
          worldX, worldY, this.player.x, this.player.y
        )

        // Don't spawn too close to player
        if (distToPlayer > 150) {
          return { x: worldX, y: worldY }
        }
      }
    }

    return null
  }

  /**
   * Perform initial creature spawns
   */
  private performInitialSpawn(): void {
    this.zones.forEach(zone => {
      const numToSpawn = Math.min(
        zone.maxCreatures,
        1 + Math.floor(Math.random() * zone.maxCreatures)
      )

      for (let i = 0; i < numToSpawn; i++) {
        this.spawnCreatureInZone(zone)
      }
    })
  }

  /**
   * Spawn a creature in a specific zone
   */
  private spawnCreatureInZone(zone: SpawnZone): Creature | null {
    if (zone.currentCreatures >= zone.maxCreatures) {
      return null
    }

    const position = this.findSpawnPosition(zone)
    if (!position) {
      return null
    }

    // Get creature type based on zone location
    const zoneX = Math.floor(zone.x / SPAWN_CONFIG.ZONE_SIZE)
    const zoneY = Math.floor(zone.y / SPAWN_CONFIG.ZONE_SIZE)
    const creatureType = CreatureFactory.getRandomType(zoneX, zoneY)

    // Create creature
    const creature = new Creature(
      this.scene,
      position.x,
      position.y,
      creatureType,
      this.player
    )

    // Add to group
    this.creatureGroup.add(creature as unknown as Phaser.GameObjects.GameObject)

    // Track spawned creature
    const spawnedCreature: SpawnedCreature = {
      creature,
      zone
    }
    this.spawnedCreatures.push(spawnedCreature)

    // Set up death callback
    creature.onDeath = () => {
      this.handleCreatureDeath(spawnedCreature)
    }

    zone.currentCreatures++

    return creature
  }

  /**
   * Handle creature death and respawn timer
   */
  private handleCreatureDeath(spawnedCreature: SpawnedCreature): void {
    const { creature } = spawnedCreature

    // Schedule respawn
    spawnedCreature.respawnTimer = this.scene.time.delayedCall(
      creature.definition.respawnTime,
      () => {
        this.respawnCreature(spawnedCreature)
      }
    )
  }

  /**
   * Respawn a creature
   */
  private respawnCreature(spawnedCreature: SpawnedCreature): void {
    const { creature } = spawnedCreature
    const zone = spawnedCreature.zone

    // Remove old creature
    const index = this.spawnedCreatures.indexOf(spawnedCreature)
    if (index !== -1) {
      this.spawnedCreatures.splice(index, 1)
    }

    // Clean up old creature
    creature.destroy()
    zone.currentCreatures--

    // Spawn new creature
    this.spawnCreatureInZone(zone)
  }

  /**
   * Update all creatures
   */
  public update(delta: number): void {
    this.spawnedCreatures.forEach(sc => {
      if (sc.creature.isAlive()) {
        sc.creature.update(delta)
      }
    })
  }

  /**
   * Get all living creatures
   */
  public getCreatures(): Creature[] {
    return this.spawnedCreatures
      .filter(sc => sc.creature.isAlive())
      .map(sc => sc.creature)
  }

  /**
   * Get creature at specific position (for targeting)
   */
  public getCreatureAt(x: number, y: number, radius: number = 30): Creature | null {
    for (const sc of this.spawnedCreatures) {
      if (!sc.creature.isAlive()) continue

      const dist = Phaser.Math.Distance.Between(x, y, sc.creature.x, sc.creature.y)
      if (dist <= radius) {
        return sc.creature
      }
    }
    return null
  }

  /**
   * Get closest creature to a point within range
   */
  public getClosestCreature(x: number, y: number, maxRange: number): Creature | null {
    let closest: Creature | null = null
    let closestDist = maxRange

    for (const sc of this.spawnedCreatures) {
      if (!sc.creature.isAlive()) continue

      const dist = Phaser.Math.Distance.Between(x, y, sc.creature.x, sc.creature.y)
      if (dist < closestDist) {
        closest = sc.creature
        closestDist = dist
      }
    }

    return closest
  }

  /**
   * Clean up
   */
  public destroy(): void {
    this.spawnedCreatures.forEach(sc => {
      if (sc.respawnTimer) {
        sc.respawnTimer.destroy()
      }
      sc.creature.destroy()
    })
    this.spawnedCreatures = []
  }
}
